/**
 * Executive Dashboard chart primitives.
 *
 * NO CHART LIBRARY — the platform's own rule (see project_management/portfolio/exdCharts.tsx,
 * which this deliberately does NOT import: §1.7, no code crosses a product folder). Every
 * mark is inline SVG built by a small function, so the screen stays self-contained.
 *
 * Three forms live here, and one hover layer they all share:
 *
 *   Bubbles    circles packed by area — value is AREA, not radius (sqrt), so a vendor with
 *              four times the spend reads as four times the ink, not sixteen
 *   Donut      part-to-whole as a ring, with the hero number in the hole
 *   Timeline   dated marks on a month axis, beeswarm-stacked when they share a date
 *   useTip     ONE floating tooltip for the whole screen, portalled to <body> so no card's
 *              overflow can clip it
 *
 * Colour comes in from the caller. This file never chooses a disposition colour (§1.9.3).
 * Circles are FLAT — no gradient or shadow; the owner asked for no depth.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/* ── tooltip ──────────────────────────────────────────────────────────────────────────── */

export interface Tip {
  /** Attach to the mark: `onPointerEnter={(e) => tip.show(e, <…/>)}` */
  show: (e: React.PointerEvent | React.FocusEvent, content: ReactNode) => void;
  move: (e: React.PointerEvent) => void;
  hide: () => void;
  /** Render this once, anywhere in the tree. */
  node: ReactNode;
}

export function useTip(): Tip {
  const [state, setState] = useState<{ x: number; y: number; content: ReactNode } | null>(null);
  const show = useCallback((e: React.PointerEvent | React.FocusEvent, content: ReactNode) => {
    const p = 'clientX' in e ? { x: e.clientX, y: e.clientY } : centre(e.currentTarget as Element);
    setState({ ...p, content });
  }, []);
  const move = useCallback((e: React.PointerEvent) => {
    setState((s) => (s ? { ...s, x: e.clientX, y: e.clientY } : s));
  }, []);
  const hide = useCallback(() => setState(null), []);
  const node = state
    ? createPortal(<TipBox x={state.x} y={state.y}>{state.content}</TipBox>, document.body)
    : null;
  return { show, move, hide, node };
}

function centre(el: Element) {
  const b = el.getBoundingClientRect();
  return { x: b.left + b.width / 2, y: b.top + b.height / 2 };
}

function TipBox({ x, y, children }: { x: number; y: number; children: ReactNode }) {
  /* Flip to the left of the cursor near the right edge, above it near the bottom. */
  const W = 290, H = 170, pad = 14;
  const left = x + pad + W > window.innerWidth ? x - pad - W : x + pad;
  const top = y + pad + H > window.innerHeight ? y - pad - H : y + pad;
  return (
    <div className="exd2-tip" style={{ left, top, width: W }} role="tooltip">
      {children}
    </div>
  );
}

/** The standard tooltip body: a title row, then value-first rows. */
export function TipRows({ title, sub, rows, swatch }: {
  title: ReactNode; sub?: ReactNode; rows: [ReactNode, ReactNode][]; swatch?: string;
}) {
  return (
    <>
      <div className="t-ti">
        {swatch ? <span className="t-sw" style={{ background: swatch }} /> : null}
        <span>{title}</span>
      </div>
      {sub ? <div className="t-sub">{sub}</div> : null}
      <div className="t-rows">
        {rows.map(([k, v], i) => (
          <div key={i} className="t-row"><b>{v}</b><span>{k}</span></div>
        ))}
      </div>
    </>
  );
}

/* ── packing ──────────────────────────────────────────────────────────────────────────── */

export interface BubbleItem {
  key: string;
  /** Shown inside the circle when it fits. */
  label: string;
  /** Second line inside the circle, usually the value. */
  sub?: string;
  value: number;
  fill: string;
  /** Text colour ON the fill. */
  ink: string;
  tip?: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  dimmed?: boolean;
}

interface Placed { x: number; y: number; r: number }

/**
 * Greedy spiral packing. Largest first at the origin; each next circle walks an Archimedean
 * spiral from the centre and stops at the first spot where it touches nothing. Steps are
 * scaled to the SMALLEST radius so small circles find the gaps between big ones. Not
 * optimal, but tight enough for the few dozen items a dashboard card holds, and it needs no
 * physics loop — the layout is deterministic, so a re-render never makes circles drift.
 */
function pack(radii: number[], gap: number): Placed[] {
  const order = radii.map((r, i) => [r, i] as const).sort((a, b) => b[0] - a[0]);
  const out: Placed[] = radii.map((r) => ({ x: 0, y: 0, r }));
  const placed: Placed[] = [];
  const minR = Math.max(1, Math.min(...radii));
  order.forEach(([r, i], k) => {
    if (k === 0) {
      out[i] = { x: 0, y: 0, r };
      placed.push(out[i]);
      return;
    }
    for (let t = 1; t < 40000; t++) {
      const ang = t * 0.17;
      const dist = t * 0.045 * minR;
      const x = dist * Math.cos(ang), y = dist * Math.sin(ang);
      const clear = placed.every((p) => Math.hypot(p.x - x, p.y - y) >= p.r + r + gap);
      if (clear) {
        out[i] = { x, y, r };
        placed.push(out[i]);
        return;
      }
    }
  });
  /* Tighten: pull every circle toward the centre while it touches nothing. The spiral
     leaves slack where it stepped past a gap; this closes it. */
  for (let pass = 0; pass < 80; pass++) {
    let moved = false;
    for (let i = 0; i < out.length; i++) {
      const c = out[i]!;
      const d = Math.hypot(c.x, c.y);
      if (d < 0.5) continue;
      const step = Math.min(d, minR * 0.15);
      const nx = c.x - (c.x / d) * step, ny = c.y - (c.y / d) * step;
      const clear = out.every((o, j) => j === i || Math.hypot(o.x - nx, o.y - ny) >= o.r + c.r + gap);
      if (clear) { c.x = nx; c.y = ny; moved = true; }
    }
    if (!moved) break;
  }
  return out;
}

/** Split a label onto two lines at a space when it will not fit on one. */
function wrap2(label: string, chars: number): string[] {
  if (label.length <= chars) return [label];
  const cut = label.lastIndexOf(' ', chars);
  if (cut < 2 || chars < 5) return [`${label.slice(0, Math.max(3, chars - 1))}…`];
  const rest = label.slice(cut + 1);
  return [label.slice(0, cut), rest.length > chars ? `${rest.slice(0, Math.max(3, chars - 1))}…` : rest];
}

/* ── bubbles ──────────────────────────────────────────────────────────────────────────── */

export function Bubbles({ items, height = 320, tip, maxLabelChars = 18 }: {
  items: BubbleItem[]; height?: number; tip: Tip; maxLabelChars?: number;
}) {
  const layout = useMemo(() => {
    const live = items.filter((i) => i.value > 0);
    if (!live.length) return null;
    /* Radii sized so the circles' total AREA is about half the target canvas — the rest is
       the air packing leaves. sqrt keeps area, not radius, proportional to the value. */
    const target = 640 * height * 0.52;
    const total = live.reduce((s, i) => s + i.value, 0);
    /* Capped so a lone circle does not swallow the card. */
    const cap = height * 0.42;
    const radii = live.map((i) => Math.min(cap, Math.max(9, Math.sqrt((i.value / total) * target / Math.PI))));
    const pos = pack(radii, 3);
    const minX = Math.min(...pos.map((p) => p.x - p.r)), maxX = Math.max(...pos.map((p) => p.x + p.r));
    const minY = Math.min(...pos.map((p) => p.y - p.r)), maxY = Math.max(...pos.map((p) => p.y + p.r));
    return { live, pos, box: [minX - 6, minY - 6, maxX - minX + 12, maxY - minY + 12] as const };
  }, [items, height]);

  if (!layout) return <div className="exd2-nodata">Nothing in scope</div>;
  const { live, pos, box } = layout;
  /* Text is set in viewBox units. Scale it so it reads at roughly 12px on screen. */
  const scale = Math.max(box[2] / 640, box[3] / height);

  return (
    <svg className="exd2-bubbles" viewBox={box.join(' ')} style={{ height }} preserveAspectRatio="xMidYMid meet">
      {live.map((it, i) => {
        const p = pos[i] ?? { x: 0, y: 0, r: 9 };
        const fs = Math.min(13 * scale, p.r / 3.2);
        const chars = Math.floor((p.r * 1.7) / (fs * 0.56));
        const fits = chars >= 4 && fs >= 8 * scale;
        const lines = wrap2(it.label.slice(0, maxLabelChars * 2), chars);
        const showSub = fits && it.sub && p.r > fs * 2.6 && it.sub.length <= chars;
        /* vertical centring of 1–3 lines: label line(s) then the value */
        const nLines = lines.length + (showSub ? 1 : 0);
        const lh = fs * 1.08;
        const y0 = p.y - ((nLines - 1) * lh) / 2;
        return (
          <g key={it.key}
             className={`exd2-bub${it.onClick ? ' click' : ''}${it.selected ? ' sel' : ''}${it.dimmed ? ' dim' : ''}`}
             tabIndex={0}
             onPointerEnter={(e) => it.tip && tip.show(e, it.tip)}
             onPointerMove={tip.move}
             onPointerLeave={tip.hide}
             onFocus={(e) => it.tip && tip.show(e, it.tip)}
             onBlur={tip.hide}
             onClick={() => { if (it.onClick) { tip.hide(); it.onClick(); } }}
             onKeyDown={(e) => { if (it.onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); tip.hide(); it.onClick(); } }}>
            <circle cx={p.x} cy={p.y} r={p.r} fill={it.fill} />
            {it.selected ? <circle cx={p.x} cy={p.y} r={p.r + 2} fill="none" stroke="var(--exd2-ink)" strokeWidth={2 * scale} /> : null}
            {fits ? lines.map((ln, k) => (
              <text key={k} x={p.x} y={y0 + k * lh} textAnchor="middle" dominantBaseline="middle"
                    fontSize={fs} fontWeight={600} fill={it.ink}>
                {ln}
              </text>
            )) : null}
            {showSub ? (
              <text x={p.x} y={y0 + lines.length * lh} textAnchor="middle" dominantBaseline="middle"
                    fontSize={fs * 0.82} fill={it.ink} opacity={0.82} fontFamily="'Roboto Mono', ui-monospace, monospace">
                {it.sub}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/* ── donut ────────────────────────────────────────────────────────────────────────────── */

export interface DonutPart {
  key: string;
  value: number;
  fill: string;
  tip?: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  dimmed?: boolean;
}

export function Donut({ parts, size = 200, thickness = 0.34, centre, tip }: {
  parts: DonutPart[]; size?: number; thickness?: number;
  centre?: { top: ReactNode; bot?: ReactNode }; tip: Tip;
}) {
  const live = parts.filter((p) => p.value > 0);
  const total = live.reduce((s, p) => s + p.value, 0);
  const c = size / 2, R = c - 4, ir = R * (1 - thickness);
  let a0 = -Math.PI / 2;
  const arcs = live.map((p) => {
    const span = total ? (p.value / total) * Math.PI * 2 : 0;
    const a1 = a0 + span;
    const big = span > Math.PI ? 1 : 0;
    const full = span >= Math.PI * 2 - 1e-6;
    /* A single 100% part cannot be drawn as one arc — the end meets the start. Two halves. */
    const d = full
      ? `M${c} ${c - R} A${R} ${R} 0 1 1 ${c} ${c + R} A${R} ${R} 0 1 1 ${c} ${c - R} Z` +
        ` M${c} ${c - ir} A${ir} ${ir} 0 1 0 ${c} ${c + ir} A${ir} ${ir} 0 1 0 ${c} ${c - ir} Z`
      : `M${c + R * Math.cos(a0)} ${c + R * Math.sin(a0)}` +
        ` A${R} ${R} 0 ${big} 1 ${c + R * Math.cos(a1)} ${c + R * Math.sin(a1)}` +
        ` L${c + ir * Math.cos(a1)} ${c + ir * Math.sin(a1)}` +
        ` A${ir} ${ir} 0 ${big} 0 ${c + ir * Math.cos(a0)} ${c + ir * Math.sin(a0)} Z`;
    const mid = a0 + span / 2;
    a0 = a1;
    return { p, d, mid, full };
  });
  return (
    <svg className="exd2-donut" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {arcs.map(({ p, d, mid, full }) => (
        <path key={p.key} d={d} fill={p.fill}
              className={`exd2-arc${p.onClick ? ' click' : ''}${p.selected ? ' sel' : ''}${p.dimmed ? ' dim' : ''}`}
              fillRule={full ? 'evenodd' : undefined}
              /* the surface gap between segments — a stroke in the surface colour, never a border */
              stroke="var(--exd2-panel)" strokeWidth={2}
              style={{ ['--dx' as string]: `${Math.cos(mid) * 4}px`, ['--dy' as string]: `${Math.sin(mid) * 4}px` }}
              tabIndex={0}
              onPointerEnter={(e) => p.tip && tip.show(e, p.tip)}
              onPointerMove={tip.move}
              onPointerLeave={tip.hide}
              onFocus={(e) => p.tip && tip.show(e, p.tip)}
              onBlur={tip.hide}
              onClick={() => { if (p.onClick) { tip.hide(); p.onClick(); } }}
              onKeyDown={(e) => { if (p.onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); tip.hide(); p.onClick(); } }} />
      ))}
      {centre ? (
        <>
          <text x={c} y={centre.bot ? c - size * 0.02 : c} textAnchor="middle" dominantBaseline="middle"
                fontSize={size * 0.15} fontWeight={600} fill="var(--exd2-ink)" letterSpacing="-.02em">
            {centre.top}
          </text>
          {centre.bot ? (
            <text x={c} y={c + size * 0.11} textAnchor="middle" dominantBaseline="middle"
                  fontSize={size * 0.062} fill="var(--exd2-ink-2)" fontWeight={500}>
              {centre.bot}
            </text>
          ) : null}
        </>
      ) : null}
    </svg>
  );
}

/* ── timeline ─────────────────────────────────────────────────────────────────────────── */

export interface TimelineItem {
  key: string;
  /** ISO date, YYYY-MM-DD. */
  date: string;
  value: number;
  fill: string;
  label: string;
  tip?: ReactNode;
}

const DAY = 86400000;
const iso = (d: Date) => d.toISOString().slice(0, 10);
const parse = (s: string) => new Date(`${s}T00:00:00Z`).getTime();
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Dated marks on a month axis. Marks that share a date (or nearly) are beeswarm-stacked:
 * each is dropped into the lowest slot where it touches nothing already placed, so 21
 * renewals on 2027-07-31 become a readable column rather than one blob.
 */
export function Timeline({ items, tip, width = 960 }: { items: TimelineItem[]; tip: Tip; width?: number }) {
  const [ref, w] = useWidth(width);
  const layout = useMemo(() => {
    const dated = items.filter((i) => /^\d{4}-\d{2}-\d{2}$/.test(i.date));
    if (!dated.length) return null;
    const today = parse(iso(new Date()));
    const ts = dated.map((i) => parse(i.date));
    let t0 = Math.min(today, ...ts), t1 = Math.max(today, ...ts);
    /* Pad to whole months either side so the axis starts and ends on a label. */
    const s = new Date(t0); s.setUTCDate(1); s.setUTCMonth(s.getUTCMonth() - 1);
    const e = new Date(t1); e.setUTCDate(1); e.setUTCMonth(e.getUTCMonth() + 2);
    t0 = s.getTime(); t1 = e.getTime();
    const padL = 12, padR = 12, axisH = 42, top = 18;
    const plotW = Math.max(200, w - padL - padR);
    const x = (t: number) => padL + ((t - t0) / (t1 - t0)) * plotW;
    const maxV = Math.max(1, ...dated.map((i) => i.value));
    const rOf = (v: number) => 7 + Math.sqrt(Math.max(0, v) / maxV) * 15;

    /* beeswarm: place each mark, largest first, at its x and the lowest free y */
    const placed: { x: number; y: number; r: number; it: TimelineItem }[] = [];
    [...dated].sort((a, b) => b.value - a.value).forEach((it) => {
      const cx = x(parse(it.date)), r = rOf(it.value);
      let cy = top + r;
      for (let guard = 0; guard < 400; guard++) {
        const hit = placed.find((p) => Math.hypot(p.x - cx, p.y - cy) < p.r + r + 3);
        if (!hit) break;
        cy = hit.y + Math.sqrt(Math.max(1, (hit.r + r + 3) ** 2 - (hit.x - cx) ** 2)) + 0.5;
      }
      placed.push({ x: cx, y: cy, r, it });
    });
    const plotH = Math.max(120, ...placed.map((p) => p.y + p.r)) + 14;

    /* month ticks; label every month if there is room, else every third */
    const months: { t: number; label: string; year: boolean }[] = [];
    for (const d = new Date(t0); d.getTime() < t1; d.setUTCMonth(d.getUTCMonth() + 1)) {
      months.push({ t: d.getTime(), label: MONTHS[d.getUTCMonth()] ?? '', year: d.getUTCMonth() === 0 || d.getTime() === t0 });
    }
    const every = plotW / months.length < 34 ? 3 : 1;
    /* how many marks per month — the density strip under the axis */
    const density = months.map((m, i) => {
      const end = months[i + 1]?.t ?? t1;
      return dated.filter((it) => { const t = parse(it.date); return t >= m.t && t < end; }).length;
    });
    const maxD = Math.max(1, ...density);
    return { placed, months, every, density, maxD, x, t0, t1, today, plotH, axisH, padL, plotW };
  }, [items, w]);

  return (
    <div ref={ref} className="exd2-timeline-wrap">
      {!layout ? <div className="exd2-nodata">No renewal carries a date</div> : (
        <svg className="exd2-timeline" width="100%" height={layout.plotH + layout.axisH}
             viewBox={`0 0 ${w} ${layout.plotH + layout.axisH}`}>
          {/* alternate quarter shading, recessive */}
          {layout.months.map((m, i) => {
            const q = Math.floor(new Date(m.t).getUTCMonth() / 3);
            const end = layout.months[i + 1]?.t ?? layout.t1;
            return q % 2 === 0 ? (
              <rect key={m.t} x={layout.x(m.t)} y={0} width={layout.x(end) - layout.x(m.t)} height={layout.plotH}
                    fill="var(--exd2-rule-soft)" opacity={0.55} />
            ) : null;
          })}
          {/* month rules + labels */}
          {layout.months.map((m, i) => (
            <g key={m.t}>
              <line x1={layout.x(m.t)} x2={layout.x(m.t)} y1={0} y2={layout.plotH} stroke="var(--exd2-rule)" strokeWidth={1} />
              {i % layout.every === 0 ? (
                <text x={layout.x(m.t) + 4} y={layout.plotH + 14} fontSize={10.5} fill="var(--exd2-ink-3)" fontWeight={600}>
                  {m.label}{m.year ? ` ${new Date(m.t).getUTCFullYear()}` : ''}
                </text>
              ) : null}
              {/* density strip */}
              {layout.density[i] ? (
                <rect x={layout.x(m.t) + 2} y={layout.plotH + 22}
                      width={Math.max(2, layout.x(layout.months[i + 1]?.t ?? layout.t1) - layout.x(m.t) - 4)}
                      height={Math.max(3, (layout.density[i] / layout.maxD) * 14)}
                      rx={2} fill="var(--exd2-slate)" opacity={0.55}>
                  <title>{`${layout.density[i]} renewal${layout.density[i] === 1 ? '' : 's'} in ${m.label} ${new Date(m.t).getUTCFullYear()}`}</title>
                </rect>
              ) : null}
            </g>
          ))}
          <line x1={layout.padL} x2={layout.padL + layout.plotW} y1={layout.plotH} y2={layout.plotH}
                stroke="var(--exd2-rule)" strokeWidth={1} />
          {/* today */}
          <g>
            <line x1={layout.x(layout.today)} x2={layout.x(layout.today)} y1={0} y2={layout.plotH}
                  stroke="var(--exd2-accent)" strokeWidth={1.5} />
            <text x={layout.x(layout.today) + 5} y={12} fontSize={10} fontWeight={700} fill="var(--exd2-accent)"
                  letterSpacing=".06em">TODAY</text>
          </g>
          {/* marks */}
          {layout.placed.map((p) => (
            <g key={p.it.key} className="exd2-tmark" tabIndex={0}
               onPointerEnter={(e) => p.it.tip && tip.show(e, p.it.tip)}
               onPointerMove={tip.move}
               onPointerLeave={tip.hide}
               onFocus={(e) => p.it.tip && tip.show(e, p.it.tip)}
               onBlur={tip.hide}>
              {/* hit target bigger than the mark */}
              <circle cx={p.x} cy={p.y} r={Math.max(12, p.r + 4)} fill="transparent" />
              <circle cx={p.x} cy={p.y} r={p.r} fill={p.it.fill} stroke="var(--exd2-panel)" strokeWidth={2} />
            </g>
          ))}
        </svg>
      )}
    </div>
  );
}

/** Measured width of a block, so the SVG can lay out in real pixels. */
function useWidth(initial: number): [(el: HTMLDivElement | null) => void, number] {
  const [w, setW] = useState(initial);
  const ro = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLDivElement | null) => {
    ro.current?.disconnect();
    ro.current = null;
    if (!el) return;
    const measure = () => setW(Math.max(200, Math.round(el.getBoundingClientRect().width)));
    measure();
    ro.current = new ResizeObserver(measure);
    ro.current.observe(el);
  }, []);
  return [ref, w];
}

/** Months from today to a date, as a phrase. */
export function monthsAway(date: string): string {
  const t = parse(date);
  if (!isFinite(t)) return '';
  const days = Math.round((t - parse(iso(new Date()))) / DAY);
  if (days < 0) return `${Math.abs(days)} days ago`;
  if (days < 45) return `in ${days} days`;
  const m = Math.round(days / 30.44);
  return m < 24 ? `in ${m} months` : `in ${(days / 365.25).toFixed(1)} years`;
}
